// src/components/ReadjudicacionMasiva/CrearLote/CrearLoteDialog.js

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Box,
    Stepper,
    Step,
    StepLabel
} from '@mui/material';
import {
    Close as CloseIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

import FiltrosBusquedaLote from './FiltrosBusquedaLote';
import ResultadosSeleccion from './ResultadosSeleccion';
import ConfirmacionLote from './ConfirmacionLote';

const styles = {
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#5c6bc0',
        color: '#fff',
        padding: '16px',
    },
    dialogContent: {
        padding: '24px',
        minHeight: 500,
    },
    stepper: {
        backgroundColor: '#fff',
        padding: '16px 24px',
        marginTop: '0',
        marginBottom: '16px',
        borderBottom: '1px solid #e0e0e0',
    },
};

const steps = ['Buscar Expedientes', 'Seleccionar Causas', 'Confirmar Lote'];

const CrearLoteDialog = ({ open, onClose, onCreate }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [filtros, setFiltros] = useState({
        fechaAsignacionDesde: null,
        fechaAsignacionHasta: null,
        fechaInicioDesde: null,
        fechaInicioHasta: null,
        montoDesde: '',
        montoHasta: '',
        abogado: null,
        caracterPersona: null,
        tipoEjecucion: null,
        estadosIncluir: []
    });
    const [expedientes, setExpedientes] = useState([]);
    // Total real de coincidencias y filtros usados: permiten ofrecer "seleccionar
    // todos los que coinciden" cuando la búsqueda devolvió más de lo que entra en
    // pantalla. Los lotes del sistema viejo llegan a 5.459 expedientes.
    const [totalCoincidencias, setTotalCoincidencias] = useState(0);
    const [filtrosUsados, setFiltrosUsados] = useState(null);
    const [expedientesSeleccionados, setExpedientesSeleccionados] = useState([]);
    const [datosLote, setDatosLote] = useState({
        idModulo: null,
        idReparticion: null
    });

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
        setFiltros({
            fechaAsignacionDesde: null,
            fechaAsignacionHasta: null,
            fechaInicioDesde: null,
            fechaInicioHasta: null,
            montoDesde: '',
            montoHasta: '',
            abogado: null,
            caracterPersona: null,
            tipoEjecucion: null,
            estadosIncluir: []
        });
        setExpedientes([]);
        setExpedientesSeleccionados([]);
        setTotalCoincidencias(0);
        setFiltrosUsados(null);
        setDatosLote({
            idModulo: null,
            idReparticion: null
        });
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleBuscar = (resultados, meta = {}) => {
        setExpedientes(resultados);
        setTotalCoincidencias(meta.total ?? resultados.length);
        setFiltrosUsados(meta.filtros ?? null);
        // Actualizar datosLote con el módulo seleccionado en los filtros
        if (filtros.tipoEjecucion) {
            const idModulo = filtros.tipoEjecucion === 'tributarias' ? 1 : 2;
            setDatosLote({
                idModulo: idModulo,
                idReparticion: idModulo
            });
        }
        if (resultados.length > 0) {
            handleNext();
        }
    };

    const handleConfirmarLote = async () => {
        if (onCreate) {
            await onCreate({
                expedientes: expedientesSeleccionados,
                ...datosLote
            });
        }
        handleClose();
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <FiltrosBusquedaLote
                        filtros={filtros}
                        onFiltrosChange={setFiltros}
                        onBuscar={handleBuscar}
                    />
                );
            case 1:
                return (
                    <ResultadosSeleccion
                        expedientes={expedientes}
                        seleccionados={expedientesSeleccionados}
                        onSeleccionChange={setExpedientesSeleccionados}
                        totalCoincidencias={totalCoincidencias}
                        filtrosUsados={filtrosUsados}
                    />
                );
            case 2:
                return (
                    <ConfirmacionLote
                        expedientesSeleccionados={expedientesSeleccionados}
                        filtros={filtros}
                    />
                );
            default:
                return 'Paso desconocido';
        }
    };

    const canProceed = () => {
        switch (activeStep) {
            case 0:
                return expedientes.length > 0;
            case 1:
                return expedientesSeleccionados.length > 0;
            case 2:
                return datosLote.idModulo && datosLote.idReparticion;
            default:
                return false;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: '4px', backgroundColor: '#fff' } }}
        >
            <DialogTitle sx={styles.dialogTitle} component="div">
                <Typography variant="h6" component="span">
                    Crear Nuevo Lote de Readjudicación
                </Typography>
                <IconButton size="small" onClick={handleClose} style={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={styles.dialogContent}>
                <Stepper activeStep={activeStep} sx={styles.stepper}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box>
                    {getStepContent(activeStep)}
                </Box>
            </DialogContent>
            <DialogActions style={{ padding: 16, justifyContent: 'space-between' }}>
                <Button onClick={handleClose}>
                    Cancelar
                </Button>
                <Box>
                    <Button
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        startIcon={<ArrowBackIcon />}
                        style={{ marginRight: 8 }}
                    >
                        Atrás
                    </Button>
                    {activeStep === steps.length - 1 ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleConfirmarLote}
                            disabled={!canProceed()}
                        >
                            Crear Lote
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            endIcon={<ArrowForwardIcon />}
                            disabled={!canProceed()}
                        >
                            Siguiente
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default CrearLoteDialog;
